import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowLeft,
  Send,
  CheckCircle,
  Eye,
  XCircle,
  Clock,
  Users,
  FileText,
  Tag,
  RefreshCw,
  Mail,
  Phone,
  User,
  AlertTriangle,
} from 'lucide-react';
import { broadcastApi } from '../../../api';

const BroadcastDetailPage = () => {
  const theme = useTheme();
  const { id, broadcastId } = useParams();
  const campaignId = broadcastId || id;
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!campaignId) return;
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await broadcastApi.getRecords(campaignId);
      const data = response?.data?.data || response?.data || [];
      setRecords(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError('Failed to load broadcast records');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      queued: {
        color: theme.palette.warning.main,
        bgColor: alpha(theme.palette.warning.main, 0.1),
        icon: <Clock size={14} />,
        label: 'Queued',
      },
      sent: {
        color: theme.palette.info.main,
        bgColor: alpha(theme.palette.info.main, 0.1),
        icon: <Send size={14} />,
        label: 'Sent',
      },
      delivered: {
        color: theme.palette.success.main,
        bgColor: alpha(theme.palette.success.main, 0.1),
        icon: <CheckCircle size={14} />,
        label: 'Delivered',
      },
      read: {
        color: '#8b5cf6',
        bgColor: alpha('#8b5cf6', 0.1),
        icon: <Eye size={14} />,
        label: 'Read',
      },
      failed: {
        color: theme.palette.error.main,
        bgColor: alpha(theme.palette.error.main, 0.1),
        icon: <XCircle size={14} />,
        label: 'Failed',
      },
    };
    return configs[status] || {
      color: theme.palette.grey[500],
      bgColor: alpha(theme.palette.grey[500], 0.1),
      icon: <AlertTriangle size={14} />,
      label: status || 'Unknown',
    };
  };

  const getStatusChip = (status) => {
    const config = getStatusConfig(status);
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  const stats = useMemo(() => {
    const summary = {
      total: records.length,
      queued: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
    };
    records.forEach((record) => {
      if (summary[record.status] !== undefined) {
        summary[record.status] += 1;
      }
    });
    return summary;
  }, [records]);
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  const templateInfo = useMemo(() => {
    const first = records.find((record) => record.template_name || record.template_category);
    if (!first) return { name: 'N/A', category: 'N/A' };
    return {
      name: first.template_name || 'N/A',
      category: first.template_category || 'N/A',
    };
  }, [records]);

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: bgColor,
        border: `1px solid ${alpha(color, 0.2)}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
        },
      }}
    >
      <Avatar
        sx={{
          bgcolor: alpha(color, 0.15),
          color: color,
          width: 44,
          height: 44,
        }}
      >
        <Icon size={22} />
      </Avatar>
      <Box>
        <Typography variant="h5" fontWeight={700} color={color}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={3}
        >
          <Box>
            <Skeleton variant="text" width={220} height={34} />
            <Skeleton variant="text" width={280} />
          </Box>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={36} height={36} />
            <Skeleton variant="rounded" width={70} height={36} />
          </Stack>
        </Stack>

        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <Grid item xs={6} sm={4} md={4} key={`stat-skeleton-${idx}`}>
                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Skeleton variant="circular" width={44} height={44} />
                          <Box>
                            <Skeleton variant="text" width={60} height={28} />
                            <Skeleton variant="text" width={80} />
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="text" width={260} />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                    <TableCell>#</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Provider Ref</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, rowIndex) => (
                    <TableRow key={`detail-skel-${rowIndex}`}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Skeleton variant="circular" width={36} height={36} />
                          <Skeleton variant="text" width={120} />
                        </Stack>
                      </TableCell>
                      <TableCell><Skeleton variant="text" width={140} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={140} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={90} height={24} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <XCircle size={32} color={theme.palette.error.main} />
          </Avatar>
          <Typography variant="h6" gutterBottom>
            Something went wrong
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/broadcast/history')}>
            Back to History
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Broadcast Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View detailed delivery status for Campaign #{campaignId}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRecords} sx={{ bgcolor: 'background.paper' }}>
              <RefreshCw size={20} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/broadcast/history')}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
        </Stack>
      </Stack>

      {/* Campaign Info Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 40, height: 40 }}>
                    <Tag size={20} color={theme.palette.primary.main} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Campaign ID
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      #{campaignId}
                    </Typography>
                  </Box>
                </Stack>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), width: 40, height: 40 }}>
                    <FileText size={20} color={theme.palette.secondary.main} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Template
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {templateInfo.name}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 40, height: 40 }}>
                    <Users size={20} color={theme.palette.info.main} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Chip
                      label={templateInfo.category}
                      size="small"
                      sx={{ mt: 0.5, fontWeight: 600 }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={Users}
                    label="Total Records"
                    value={stats.total}
                    color={theme.palette.primary.main}
                    bgColor={alpha(theme.palette.primary.main, 0.05)}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={Send}
                    label="Sent"
                    value={stats.sent}
                    color={theme.palette.info.main}
                    bgColor={alpha(theme.palette.info.main, 0.05)}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={CheckCircle}
                    label="Delivered"
                    value={stats.delivered}
                    color={theme.palette.success.main}
                    bgColor={alpha(theme.palette.success.main, 0.05)}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={Eye}
                    label="Read"
                    value={stats.read}
                    color="#8b5cf6"
                    bgColor={alpha('#8b5cf6', 0.05)}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={Clock}
                    label="Queued"
                    value={stats.queued}
                    color={theme.palette.warning.main}
                    bgColor={alpha(theme.palette.warning.main, 0.05)}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatCard
                    icon={XCircle}
                    label="Failed"
                    value={stats.failed}
                    color={theme.palette.error.main}
                    bgColor={alpha(theme.palette.error.main, 0.05)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" fontWeight={600}>
              Delivery Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Individual message delivery status and information
            </Typography>
          </Box>
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Sent At</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Provider Ref</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((record, index) => (
                    <TableRow
                      key={record.id || index}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontSize: '0.875rem',
                              fontWeight: 600,
                            }}
                          >
                            {(record.customer_name || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>
                            {record.customer_name || 'N/A'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Phone size={14} color={theme.palette.text.secondary} />
                            <Typography variant="body2">
                              {record.customer_number || 'N/A'}
                            </Typography>
                          </Stack>
                          {record.customer_email && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Mail size={14} color={theme.palette.text.secondary} />
                              <Typography variant="body2" color="text.secondary">
                                {record.customer_email}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{getStatusChip(record.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(record.sent_at || record.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={record.provider_ref || 'N/A'}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {record.provider_ref || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {record.error_message ? (
                          <Tooltip title={record.error_message}>
                            <Chip
                              icon={<AlertTriangle size={14} />}
                              label="View Error"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            mx: 'auto',
                            mb: 2,
                          }}
                        >
                          <FileText size={32} color={theme.palette.primary.main} />
                        </Avatar>
                        <Typography variant="h6" gutterBottom>
                          No records found
                        </Typography>
                        <Typography color="text.secondary">
                          There are no broadcast records for this campaign yet.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {records.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={records.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BroadcastDetailPage;
